// ScrollArea: a vertical scroll container that fades its top/bottom edge only where content is hidden, so a list
// cut by pinned buttons reads as "scroll for more" instead of broken text. `reverse` anchors content to the bottom
// (chat logs: column-reverse). Pass layout styles through class/style (it is a flex column by default).
//   <ScrollArea class={s.body}>...</ScrollArea>
//   <ScrollArea reverse role="log">...</ScrollArea>
import type { ComponentChildren, JSX } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

type AriaRole = JSX.HTMLAttributes<HTMLDivElement>['role'];

export interface ScrollAreaProps {
  children: ComponentChildren;
  class?: string;
  style?: JSX.CSSProperties;
  /** Bottom-anchored (flex-direction: column-reverse), like a chat. */
  reverse?: boolean;
  /** Fade sizes in px (default 24 top, 24 bottom). 0 disables an edge. */
  fadeTop?: number;
  fadeBottom?: number;
  role?: AriaRole;
  'aria-label'?: string;
  'aria-live'?: 'polite' | 'off' | 'assertive';
}

export function ScrollArea({ children, class: cls, style, reverse, fadeTop = 24, fadeBottom = 24, ...aria }: ScrollAreaProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ top: false, bottom: false });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => {
      const range = el.scrollHeight - el.clientHeight;
      const st = Math.abs(el.scrollTop);
      const top = range > 2 && (reverse ? range - st > 2 : st > 2);
      const bottom = range > 2 && (reverse ? st > 2 : range - st > 2);
      setEdge((e) => (e.top === top && e.bottom === bottom ? e : { top, bottom }));
    };
    read();
    el.addEventListener('scroll', read, { passive: true });
    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(read); ro.observe(el); for (const c of Array.from(el.children)) ro.observe(c); }
    if (typeof MutationObserver !== 'undefined') {
      mo = new MutationObserver(() => { if (ro) for (const c of Array.from(el.children)) ro.observe(c); read(); });
      mo.observe(el, { childList: true, subtree: true });
    }
    return () => { el.removeEventListener('scroll', read); ro?.disconnect(); mo?.disconnect(); };
  }, [reverse]);
  const t = edge.top && fadeTop ? `transparent 0px, #000 ${fadeTop}px` : '#000 0px';
  const b = edge.bottom && fadeBottom ? `#000 calc(100% - ${fadeBottom}px), transparent 100%` : '#000 100%';
  const mask = edge.top || edge.bottom ? `linear-gradient(180deg, ${t}, ${b})` : undefined;
  return (
    <div
      ref={ref} class={['xm-scroll', cls].filter(Boolean).join(' ')} {...aria}
      style={{ display: 'flex', flexDirection: reverse ? 'column-reverse' : 'column', minHeight: 0, WebkitMaskImage: mask, maskImage: mask, ...style }}
    >
      {children}
    </div>
  );
}
